import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { RadioButtons, Input, Select } from '@ds';
import { InputChangeVariant } from '@design-system/elements/Input/Input.types';
import { IOption, SelectChangeVariant } from '@design-system/elements/Select/Select.types';
import { PhoneRegex } from 'src/utils/constants/common';
import styles from './PhoneInput.module.scss';

export const PHONE_FIELD_NAMES = {
  phoneType: 'phoneType',
  phoneNumber: 'phoneNumber',
  countryCode: 'countryCode',
  extension: 'extension',
};

export interface ContactPhone {
  phoneId?: number;
  phoneType?: string;
  countryCode?: string;
  phoneNumber?: string;
  extension?: string;
  isPrimary?: boolean;
}

const TITLES = {
  phoneTypeTitle: 'Phone Type',
  countryCodeTitle: 'Country Location',
  phoneNumberTitle: 'Phone Number',
  extensionTitle: 'Extension',
  primaryTitle: 'Primary',
  errorMessage: 'Required',
};

interface PhoneInputProps {
  id: number;
  refPhone?: ContactPhone;
  phoneTypeOptions: IOption[];
  isPrimary?: boolean;
  phoneNumber?: string;
  countryCode?: string;
  phoneType?: string;
  extension?: string;
  canEdit?: boolean;
  showEdited?: boolean;
  titles?: {
    phoneType?: string;
    phoneNumber?: string;
    countryCode?: string;
    extension?: string;
  } | null;
  onChangeSelect?: (index: number, selectedOption: IOption) => void;
  onChangeField?: (index: number, field: string, value: string) => void;
  onPrimaryChange?: (index: number) => void;
}

const PhoneInput = ({
  id,
  refPhone,
  isPrimary = false,
  phoneNumber: initialPhoneNumber = '',
  countryCode: initialCountryCode = '',
  phoneType: initialPhoneType = '',
  extension: initialExtension = '',
  phoneTypeOptions,
  canEdit = true,
  showEdited = true,
  titles = null,
  onChangeSelect,
  onChangeField,
  onPrimaryChange,
}: PhoneInputProps) => {
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [countryCode, setCountryCode] = useState(initialCountryCode);
  const [phoneType, setPhoneType] = useState(initialPhoneType);
  const [extension, setExtension] = useState(initialExtension);

  const checkMissingPhoneError = (
    phoneNumber: string,
    phoneType: string,
    countryCode: string,
    extension: string,
    field: string = '',
    updatedValue: string = ''
  ): boolean => {
    const isEmptyPhoneNumber =
      (!phoneNumber && field !== PHONE_FIELD_NAMES.phoneNumber) ||
      (field === PHONE_FIELD_NAMES.phoneNumber &&
        !updatedValue &&
        (phoneType || countryCode || extension || isPrimary));

    const hasOtherFields =
      (field !== PHONE_FIELD_NAMES.phoneType && phoneType) ||
      (field !== PHONE_FIELD_NAMES.countryCode && countryCode) ||
      (field !== PHONE_FIELD_NAMES.extension && extension) ||
      isPrimary ||
      updatedValue;

    return !!isEmptyPhoneNumber && !!hasOtherFields;
  };
  const [missingPhoneError, setMissingPhoneError] = useState(() =>
    checkMissingPhoneError(initialPhoneNumber, initialPhoneType, initialCountryCode, initialExtension)
  );

  const phoneNumberFieldRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPhoneNumber(initialPhoneNumber);
    setCountryCode(initialCountryCode);
    setPhoneType(initialPhoneType);
    setExtension(initialExtension);
  }, [initialPhoneNumber, initialCountryCode, initialPhoneType, initialExtension]);

  useEffect(() => {
    if (canEdit) {
      setMissingPhoneError(checkMissingPhoneError(phoneNumber, phoneType, countryCode, extension));
    }
  }, [canEdit, phoneType, phoneNumber, countryCode, extension, isPrimary]);

  const handleChange = (field: string, value: string | IOption | number): void => {
    const updatedValue = typeof value === 'string' ? value : (value as IOption)?.value;

    const newPhoneNumber = field === PHONE_FIELD_NAMES.phoneNumber ? (updatedValue as string) : phoneNumber;
    const newPhoneType = field === PHONE_FIELD_NAMES.phoneType ? (updatedValue as string) : phoneType;
    const newCountryCode = field === PHONE_FIELD_NAMES.countryCode ? (updatedValue as string) : countryCode;
    const newExtension = field === PHONE_FIELD_NAMES.extension ? (updatedValue as string) : extension;

    switch (field) {
      case PHONE_FIELD_NAMES.phoneNumber:
        setPhoneNumber(newPhoneNumber);
        break;
      case PHONE_FIELD_NAMES.phoneType:
        setPhoneType(newPhoneType);
        break;
      case PHONE_FIELD_NAMES.countryCode:
        setCountryCode(newCountryCode);
        break;
      case PHONE_FIELD_NAMES.extension:
        setExtension(newExtension);
        break;
      default:
        break;
    }

    const shouldPhoneError = checkMissingPhoneError(
      newPhoneNumber,
      newPhoneType,
      newCountryCode,
      newExtension,
      field,
      updatedValue as string
    );

    setMissingPhoneError(shouldPhoneError);

    if (onChangeSelect && field === PHONE_FIELD_NAMES.phoneType) {
      onChangeSelect(id as number, value as IOption);
    } else if (onChangeField) {
      onChangeField(id as number, field, String(value));
    }
  };

  const handleKeyPress = (event: { key: string; preventDefault: () => void }) => {
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'];
    if (allowedKeys.includes(event.key)) {
      return;
    }

    if (!PhoneRegex.test(event.key)) {
      event.preventDefault();
    }
  };

  const inputElementClass = 'w-full md:w-[224px]';

  return (
    <div className={styles['contact-phone-group']}>
      <div className={inputElementClass}>
        <Select
          onChange={(e) => {
            handleChange(PHONE_FIELD_NAMES.phoneType, e as IOption);
          }}
          options={phoneTypeOptions}
          placeholder=""
          label={titles?.phoneType || TITLES.phoneTypeTitle}
          selectedOption={{ value: phoneType, label: phoneType }}
          hasChanged={
            showEdited
              ? !!refPhone && phoneTypeOptions?.length > 0 && (phoneType || '') !== (refPhone.phoneType || '')
              : false
          }
          showClear={phoneType !== ''}
          disabled={!canEdit}
          selectChangedStyle={SelectChangeVariant.Secondary}
        />
      </div>
      <div className={inputElementClass}>
        <Input
          label={titles?.countryCode || TITLES.countryCodeTitle}
          value={countryCode || ''}
          onKeyDown={handleKeyPress}
          onChange={(e) => handleChange(PHONE_FIELD_NAMES.countryCode, e.target.value)}
          hasChanged={showEdited ? !!refPhone && (countryCode || '') !== (refPhone.countryCode || '') : false}
          pattern={PhoneRegex.source}
          disabled={!canEdit}
          inputChangedStyle={InputChangeVariant.Secondary}
        />
      </div>
      <div
        className={classNames(inputElementClass, { [styles['error']]: missingPhoneError })}
        ref={phoneNumberFieldRef}
      >
        <Input
          label={titles?.phoneNumber || TITLES.phoneNumberTitle}
          value={phoneNumber || ''}
          onKeyDown={handleKeyPress}
          onChange={(e) => {
            handleChange(PHONE_FIELD_NAMES.phoneNumber, e.target.value);
          }}
          hasChanged={showEdited ? !!refPhone && (phoneNumber || '') !== (refPhone.phoneNumber || '') : false}
          pattern={PhoneRegex.source}
          disabled={!canEdit}
          inputChangedStyle={InputChangeVariant.Secondary}
        />
        {missingPhoneError && <span className="error-message">{TITLES.errorMessage}</span>}
      </div>
      <div className={inputElementClass}>
        <Input
          label={titles?.extension || TITLES.extensionTitle}
          value={extension || ''}
          onKeyDown={handleKeyPress}
          onChange={(e) => handleChange(PHONE_FIELD_NAMES.extension, e.target.value)}
          hasChanged={showEdited ? !!refPhone && (extension || '') !== (refPhone.extension || '') : false}
          pattern={PhoneRegex.source}
          disabled={!canEdit}
          inputChangedStyle={InputChangeVariant.Secondary}
        />
      </div>
      {onPrimaryChange && (
        <div className={classNames(inputElementClass, 'flex')}>
          <RadioButtons
            name="phoneInputRadioButton"
            selectedOption={isPrimary ? TITLES.primaryTitle : ''}
            options={[{ label: TITLES.primaryTitle, value: TITLES.primaryTitle }]}
            type={showEdited ? (!refPhone?.isPrimary == !isPrimary ? 'secondary' : 'highlighted') : 'secondary'}
            disabled={!canEdit}
            onChange={() => onPrimaryChange(id)}
          />
        </div>
      )}
    </div>
  );
};

export default PhoneInput;
